import { useEffect, useState } from 'react';
import { fetchManufacturingEquipmentImageUrl } from '../../../api/manufacturingEquipment';
import {
  EquipmentManagementStandard,
  EquipmentType,
} from '../../../types/manufacturingEquipment';
import '../../equipment/ManagementStandard.css';

interface Props {
  standard: EquipmentManagementStandard;
  equipmentType?: EquipmentType;
}

function ManagementStandardPreviewView({
  standard,
  equipmentType = 'MANUFACTURING',
}: Props) {
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const writtenDate = standard.writtenDate?.slice(0, 10) || '';
  const approvedDate = standard.approvedAt?.slice(0, 10) || '';
  const inspections = standard.inspections ?? [];
  const lubrications = standard.lubrications ?? [];

  useEffect(() => {
    let revoked = false;
    let objectUrl: string | null = null;
    if (!standard.hasEquipmentImage || !standard.equipmentId) {
      setImageUrl(null);
      return undefined;
    }
    void fetchManufacturingEquipmentImageUrl(standard.equipmentId, equipmentType)
      .then((url) => {
        if (revoked) {
          window.URL.revokeObjectURL(url);
          return;
        }
        objectUrl = url;
        setImageUrl(url);
      })
      .catch(() => {
        if (!revoked) setImageUrl(null);
      });
    return () => {
      revoked = true;
      if (objectUrl) window.URL.revokeObjectURL(objectUrl);
    };
  }, [standard.equipmentId, standard.hasEquipmentImage, equipmentType]);

  return (
    <div className="mgmt-standard">
      <div className="mgmt-standard__sheet">
        <div className="mgmt-standard__top">
          <div>
            <div className="mgmt-standard__legend">
              <strong>범례</strong>
              <div>점검주기: D(매일) / W(매주) / M(매월) / Y(매년)</div>
              <div>점검시기: 운(운전시) / 휴(휴지시) / 비(비운전시)</div>
            </div>
            <div className="mgmt-standard__meta" style={{ marginTop: 10 }}>
              <label>설비명</label>
              <input value={standard.equipmentName ?? ''} readOnly />
              <label>설비번호</label>
              <input value={standard.equipmentCode ?? ''} readOnly />
              <label>작성일자</label>
              <input type="date" value={writtenDate} readOnly />
              {standard.approvedAt ? (
                <>
                  <label>승인일자</label>
                  <input type="date" value={approvedDate} readOnly />
                </>
              ) : null}
              <label>작성자</label>
              <input value={standard.createdUserName ?? '-'} readOnly />
            </div>
          </div>

          <div className="mgmt-standard__sign">
            <div className="mgmt-standard__sign-cell">
              <strong>작성</strong>
              <span>{standard.createdUserName || '-'}</span>
            </div>
            <div className="mgmt-standard__sign-cell">
              <strong>승인</strong>
              <span>{standard.approvedUserName || '-'}</span>
            </div>
          </div>
        </div>

        <div className="mgmt-standard__mid">
          <section className="mgmt-standard__box">
            <h4 className="mgmt-standard__box-title">운전 안전 수칙</h4>
            <div className="mgmt-standard__box-body">
              <div className="mgmt-standard__field">
                <label>운전시 유의사항</label>
                <textarea rows={5} value={standard.safetyNotes || ''} readOnly />
              </div>
              <div className="mgmt-standard__field">
                <label>주요확인 사항</label>
                <textarea rows={2} value={standard.mainCheckItems || ''} readOnly />
              </div>
              <div className="mgmt-standard__field">
                <label>인수인계 사항</label>
                <textarea rows={2} value={standard.handoverNotes || ''} readOnly />
              </div>
            </div>
          </section>

          <section className="mgmt-standard__box">
            <h4 className="mgmt-standard__box-title">사진</h4>
            <div className="mgmt-standard__box-body">
              <div className="mgmt-standard__photo">
                {imageUrl ? <img src={imageUrl} alt="설비" /> : <span>기본정보의 설비 사진</span>}
              </div>
            </div>
          </section>

          <section className="mgmt-standard__box mgmt-standard__box--operation">
            <h4 className="mgmt-standard__box-title">설비운전표준</h4>
            <div className="mgmt-standard__box-body mgmt-standard__box-body--fill">
              <div className="mgmt-standard__field mgmt-standard__field--fill">
                <textarea value={standard.operationStandard || ''} readOnly />
              </div>
            </div>
          </section>
        </div>

        <div>
          <div className="mgmt-standard__section-head">
            <h4>설비점검기준</h4>
          </div>
          <div className="mgmt-standard__table-wrap">
            <table className="mgmt-standard__table">
              <thead>
                <tr>
                  <th>NO</th>
                  <th>점검개소</th>
                  <th>점검항목</th>
                  <th>점검주기</th>
                  <th>점검시기</th>
                  <th>점검방법</th>
                  <th>점검기준</th>
                  <th>이상시 조치 및 교체 절차</th>
                </tr>
              </thead>
              <tbody>
                {inspections.length === 0 ? (
                  <tr>
                    <td colSpan={8}>등록된 점검기준이 없습니다.</td>
                  </tr>
                ) : (
                  inspections.map((row, index) => (
                    <tr key={`insp-${index}`}>
                      <td className="is-no">{index + 1}</td>
                      <td>
                        <input value={row.inspectionPoint ?? ''} readOnly />
                      </td>
                      <td>
                        <input value={row.inspectionItem ?? ''} readOnly />
                      </td>
                      <td>
                        <select value={row.cycleCode ?? 'W'} disabled>
                          <option value="D">D</option>
                          <option value="W">W</option>
                          <option value="M">M</option>
                          <option value="Y">Y</option>
                        </select>
                      </td>
                      <td>
                        <select value={row.timingCode ?? '휴'} disabled>
                          <option value="운">운</option>
                          <option value="휴">휴</option>
                          <option value="비">비</option>
                        </select>
                      </td>
                      <td>
                        <input value={row.method ?? ''} readOnly />
                      </td>
                      <td>
                        <input value={row.criteria ?? ''} readOnly />
                      </td>
                      <td>
                        <input value={row.actionOnAbnormal ?? ''} readOnly />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div>
          <div className="mgmt-standard__section-head">
            <h4>윤활점검기준</h4>
          </div>
          <div className="mgmt-standard__table-wrap">
            <table className="mgmt-standard__table">
              <thead>
                <tr>
                  <th>NO</th>
                  <th>급유개소</th>
                  <th>윤활유</th>
                  <th>급유주기</th>
                  <th>급유량</th>
                  <th>급유방법</th>
                  <th>폐윤활유 처리</th>
                </tr>
              </thead>
              <tbody>
                {lubrications.length === 0 ? (
                  <tr>
                    <td colSpan={7}>등록된 윤활기준이 없습니다.</td>
                  </tr>
                ) : (
                  lubrications.map((row, index) => (
                    <tr key={`lube-${index}`}>
                      <td className="is-no">{index + 1}</td>
                      <td>
                        <input value={row.lubricationPoint ?? ''} readOnly />
                      </td>
                      <td>
                        <input value={row.lubricant ?? ''} readOnly />
                      </td>
                      <td>
                        <select value={row.cycleCode ?? 'M'} disabled>
                          <option value="D">D</option>
                          <option value="W">W</option>
                          <option value="M">M</option>
                          <option value="Y">Y</option>
                        </select>
                      </td>
                      <td>
                        <input value={row.amount ?? ''} readOnly />
                      </td>
                      <td>
                        <input value={row.method ?? ''} readOnly />
                      </td>
                      <td>
                        <input value={row.wasteDisposal ?? ''} readOnly />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ManagementStandardPreviewView;
